import React, { useEffect, useState } from "react";
import axios from "axios";
import { Chart } from "react-google-charts";
import { StandardURL } from "../App";

const TransactionC2BStatsView = () => {
  const [response, setResponse] = useState([]);
  const [loading, setLoading] = useState(true);

  const propertyId = localStorage.getItem("selectedPropertyId");
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(
          `${StandardURL}/iNyumba/landlordviewc2byearlymonthly`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { propertyid: propertyId },
          }
        );
        setResponse(data.response);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };
    fetchData();
  }, [propertyId]);

  if (loading) {
    return <div>Loading...</div>;
  }

  // Prepare data for Google Line Chart
  const lineChartData = [
    ["Month", "Total Amount (KSH)"],
    ...response.map((data) => [data.month, parseFloat(data.total_amount)]),
  ];

  // Prepare data for Google Pie Chart
  const pieChartData = [
    ["Month", "Amount"],
    ...response.map((data) => [data.month, parseFloat(data.total_amount)]),
  ];

return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 p-4 sm:p-6 lg:p-8">
      {/* --- Income Summary Table Container --- */}
      <div className="bg-white p-6 sm:p-8 rounded-xl shadow-2xl border border-gray-200 mb-10 max-w-7xl mx-auto">
        <h2 className="text-4xl font-extrabold text-gray-900 text-center mb-8 tracking-tight flex items-center justify-center">
          💰 Income Summary Report
        </h2>
        <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
          This table provides a monthly breakdown of income, including the number of payments and total amounts received.
        </p>

        <div className="overflow-x-auto relative shadow-lg rounded-lg border border-gray-100">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
              <tr>
                <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider rounded-tl-lg">Month</th>
                <th className="py-3 px-4 text-center text-xs font-semibold uppercase tracking-wider">No. of Payments</th>
                <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wider rounded-tr-lg">Amount (Ksh)</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {response.length > 0 ? (
                response.map((data, index) => (
                  <tr
                    key={index}
                    className={`${index % 2 === 0 ? "bg-gray-50" : "bg-white"} hover:bg-gray-100 transition-colors duration-150 ease-in-out`}
                  >
                    <td className="py-3 px-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {data.month}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap text-sm text-center text-gray-700 font-semibold">
                      {data.count}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap text-sm text-right font-bold text-green-600">
                      Ksh {parseFloat(data.total_amount).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="py-8 px-4 text-center text-lg text-gray-500 italic">
                    <div className="flex flex-col items-center justify-center">
                      <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                      No income data available.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- Charts Container --- */}
      <div className="flex flex-col lg:flex-row justify-center items-stretch gap-8 max-w-7xl mx-auto">
        {/* Line Chart Card */}
        <div className="flex-1 min-w-[300px] lg:min-w-[45%] bg-white p-6 sm:p-8 rounded-xl shadow-2xl border border-gray-200 flex flex-col">
          <h2 className="text-3xl font-bold text-gray-800 text-center mb-6">
            📈 Income Trend
          </h2>
          {lineChartData && lineChartData.length > 1 ? ( // Ensure there's enough data for the chart
            <Chart
              chartType="LineChart"
              width="100%"
              height="350px"
              data={lineChartData}
              options={{
                hAxis: {
                  title: "Month",
                  titleTextStyle: { color: "#333" },
                  textStyle: { color: "#555" },
                },
                vAxis: {
                  title: "Total Amount (Ksh)",
                  titleTextStyle: { color: "#333" },
                  textStyle: { color: "#555" },
                  format: "Ksh #,###", // Format y-axis labels as currency
                },
                legend: "none",
                colors: ["#3B82F6"], // Tailwind blue-500
                curveType: "function",
                pointSize: 8, // Make points more visible
                tooltip: { isHtml: true },
                chartArea: { left: 70, top: 20, width: "85%", height: "70%" },
                backgroundColor: "transparent",
              }}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-10 text-gray-500 italic">
                <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path></svg>
              No sufficient data for Line Chart.
            </div>
          )}
        </div>

        {/* Pie Chart Card */}
        <div className="flex-1 min-w-[300px] lg:min-w-[45%] bg-white p-6 sm:p-8 rounded-xl shadow-2xl border border-gray-200 flex flex-col">
          <h2 className="text-3xl font-bold text-gray-800 text-center mb-6">
            📊 Income Distribution
          </h2>
          {pieChartData && pieChartData.length > 1 ? ( // Ensure there's enough data for the chart
            <Chart
              chartType="PieChart"
              width="100%"
              height="350px"
              data={pieChartData}
              options={{
                pieHole: 0.4,
                is3D: false,
                legend: { position: "right", alignment: "center", textStyle: { color: "#555", fontSize: 13 } },
                colors: ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#06B6D4", "#6366F1", "#EC4899"], // Tailwind color palette
                tooltip: { isHtml: true },
                chartArea: { left: 20, top: 20, width: "90%", height: "85%" },
                backgroundColor: "transparent",
              }}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-10 text-gray-500 italic">
                <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"></path></svg>
              No sufficient data for Pie Chart.
            </div>
          )}
        </div>
      </div>
    </div>
  );};

export default TransactionC2BStatsView;
